import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Heading, Undo, Redo } from "lucide-react";

// Convert legacy plain-text (with blank-line paragraphs) into simple HTML.
export function textToHtml(v = "") {
  if (!v) return "";
  if (/<(p|br|ul|ol|li|h[1-4]|strong|em|b|i|u|div)\b/i.test(v)) return v; // already HTML
  return v.split(/\n{2,}/).map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`).join("");
}

const cmd = (c, val = null) => document.execCommand(c, false, val);

// Minimal dependency-free rich-text editor. value/onChange are HTML strings.
export function RichText({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = textToHtml(value || "");
    // eslint-disable-next-line
  }, []);
  const emit = () => onChange?.(ref.current?.innerHTML || "");
  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    let out;
    if (html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html.replace(/<!--[\s\S]*?-->/g, "");
      const ALLOW = /^(P|BR|UL|OL|LI|H1|H2|H3|H4|STRONG|B|EM|I|U|SPAN|DIV|A)$/i;
      tmp.querySelectorAll("*").forEach((el) => {
        if (/^(O:P|STYLE|META|LINK|SCRIPT|XML|TITLE)$/i.test(el.tagName)) { el.remove(); return; }
        [...el.attributes].forEach((a) => { if (!(el.tagName === "A" && a.name === "href")) el.removeAttribute(a.name); });
        if (!ALLOW.test(el.tagName)) el.replaceWith(...el.childNodes);
      });
      out = tmp.innerHTML.replace(/<span>|<\/span>/gi, "").replace(/(\s)?class="[^"]*"/gi, "");
    } else {
      out = (text || "").split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
    }
    document.execCommand("insertHTML", false, out);
    emit();
  };
  const btn = (icon, action, title) => (
    <button type="button" title={title} onMouseDown={(e) => { e.preventDefault(); action(); ref.current?.focus(); emit(); }}
      className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" data-testid={`rt-${title.toLowerCase().replace(/\s+/g, "-")}`}>{icon}</button>
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 px-2 py-1.5">
        {btn(<Bold className="h-4 w-4" />, () => cmd("bold"), "Bold")}
        {btn(<Italic className="h-4 w-4" />, () => cmd("italic"), "Italic")}
        {btn(<Underline className="h-4 w-4" />, () => cmd("underline"), "Underline")}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {btn(<Heading className="h-4 w-4" />, () => cmd("formatBlock", "<h3>"), "Heading")}
        {btn(<span className="px-1 text-xs font-semibold">P</span>, () => cmd("formatBlock", "<p>"), "Paragraph")}
        {btn(<List className="h-4 w-4" />, () => cmd("insertUnorderedList"), "Bullet list")}
        {btn(<ListOrdered className="h-4 w-4" />, () => cmd("insertOrderedList"), "Numbered list")}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {btn(<Undo className="h-4 w-4" />, () => cmd("undo"), "Undo")}
        {btn(<Redo className="h-4 w-4" />, () => cmd("redo"), "Redo")}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        data-testid={testid ? `${testid}-area` : "richtext-area"}
        className="prose max-w-none min-h-[280px] max-h-[52vh] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-slate-700 text-justify focus:outline-none [&_p]:mb-3 [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-blue-700 [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:font-semibold [&_h3]:text-slate-900 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_strong]:font-semibold [&_strong]:text-slate-900"
      />
    </div>
  );
}
